import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Star } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const LOGO_URL = "/logo.png";
const GOOGLE_REVIEW_URL = "https://g.page/r/CR3dspobMw_YEAE/review";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSettings();
  
  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <img 
              src={LOGO_URL} 
              alt={settings.agency_name} 
              className="h-36 w-auto mb-6 bg-white rounded-lg p-3"
            />
            <p className="text-gray-400 leading-relaxed text-sm">
              {settings.description}
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-[#D4AF37]">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/catalogue?type=vente" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Acheter
                </Link>
              </li>
              <li>
                <Link to="/catalogue?type=location" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Louer
                </Link>
              </li>
              <li>
                <Link to="/gestion" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Faire gérer son bien
                </Link>
              </li>
              <li>
                <Link to="/documents" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Déposer un dossier
                </Link>
              </li>
              <li>
                <Link to="/agence" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Notre agence
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-[#D4AF37]">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span className="text-gray-400 text-sm whitespace-pre-line">
                  {settings.address}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a href={`tel:${settings.phone?.replace(/\s/g, '')}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {settings.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a href={`mailto:${settings.email}`} className="text-gray-400 hover:text-white transition-colors text-sm">
                  {settings.email}
                </a>
              </li>
            </ul>
          </div>
          
          {/* Social & Hours */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-[#D4AF37]">Horaires</h4>
            <div className="text-gray-400 text-sm space-y-2 mb-8" data-testid="footer-hours">
              {(settings.hours || "").split("\n").filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}
            </div>

            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-5 py-2.5 text-xs uppercase tracking-wider mb-8"
              data-testid="footer-google-review"
            >
              <Star size={14} /> Déposer un avis Google
            </a>
            
            <h4 className="font-serif text-lg mb-4 text-[#D4AF37]">Suivez-nous</h4>
            <div className="flex items-center gap-4">
              {settings.facebook && (
                <a 
                  href={settings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="social-facebook"
                >
                  <Facebook size={18} />
                </a>
              )}
              {settings.instagram && (
                <a 
                  href={settings.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="social-instagram"
                >
                  <Instagram size={18} />
                </a>
              )}
              {settings.linkedin && (
                <a 
                  href={settings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="social-linkedin"
                >
                  <Linkedin size={18} />
                </a>
              )}
              {!settings.facebook && !settings.instagram && !settings.linkedin && (
                <>
                  <a 
                    href="#" 
                    className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                    data-testid="social-facebook"
                  >
                    <Facebook size={18} />
                  </a>
                  <a 
                    href="#" 
                    className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                    data-testid="social-instagram"
                  >
                    <Instagram size={18} />
                  </a>
                  <a 
                    href="#" 
                    className="w-10 h-10 border border-gray-700 rounded-full flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                    data-testid="social-linkedin"
                  >
                    <Linkedin size={18} />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {currentYear} {settings.agency_name}. Tous droits réservés.
            <span className="mx-2 text-gray-700">|</span>
            <span data-testid="footer-siret">SIRET : 109 512 798 00017</span>
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/mentions-legales" className="text-gray-500 hover:text-white transition-colors" data-testid="footer-legal-link">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" className="text-gray-500 hover:text-white transition-colors" data-testid="footer-privacy-link">
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
